/**
 * Thin client-side wrapper around the /api/v1 surface.
 *
 * - Always sends cookies (`credentials: 'include'`) so the httpOnly access /
 *   refresh cookies ride along.
 * - Parses the server's `{ error: { code, message } }` envelope into a typed
 *   {@link ApiError}.
 * - For authenticated calls (`auth: true`), a 401 triggers a single refresh
 *   attempt; if that fails the browser is sent to /login.
 */

import { isLocale } from "@/i18n/config";
import { clearMe } from "@/lib/auth-store";

const BASE = "/api/v1";

export interface ApiErrorBody {
  code: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiFetchOptions extends Omit<RequestInit, "body"> {
  /** JSON-serializable request body. */
  body?: unknown;
  /** Enable the 401 → refresh → retry → /login flow (for authed endpoints). */
  auth?: boolean;
}

async function parse<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const body = (data?.error ?? {}) as Partial<ApiErrorBody>;
    throw new ApiError(
      body.code ?? "UNKNOWN",
      body.message ?? res.statusText ?? "Request failed",
      res.status,
    );
  }
  return data as T;
}

async function rawFetch(path: string, options: ApiFetchOptions): Promise<Response> {
  // `auth` falls into `rest` and is ignored by fetch (not a RequestInit field).
  const { body, headers, ...rest } = options;
  return fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...rest,
  });
}

/** In-flight refresh shared by all concurrent callers (single-flight). */
let refreshInFlight: Promise<boolean> | null = null;

/**
 * POST /auth/refresh with the httpOnly refresh cookie; true when a new access
 * token was issued. Shared by apiFetch's 401 retry flow and the realtime
 * socket's 4401 re-auth (see useRealtime).
 *
 * Single-flight: the WS 4401 close and an apiFetch 401 fire together at every
 * token expiry, and two parallel refreshes with the same rotating cookie trip
 * the server's replay guard (401 → /login redirect, or a whole-family
 * revoke). All concurrent callers therefore await one shared request.
 */
export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= fetch(`${BASE}/auth/refresh`, {
    method: "POST",
    credentials: "include",
  })
    .then((res) => res.ok)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  let res = await rawFetch(path, options);

  if (res.status === 401 && options.auth) {
    if (await refreshSession()) {
      res = await rawFetch(path, options);
    } else {
      // Keep the locale prefix ("as-needed": en at bare paths) — a hard
      // redirect bypasses the locale-aware router.
      if (typeof window !== "undefined") {
        clearMe();
        const prefix = window.location.pathname.split("/")[1];
        window.location.href = prefix && isLocale(prefix) && prefix !== "en" ? `/${prefix}/login` : "/login";
      }
      throw new ApiError("UNAUTHORIZED", "Please sign in again.", 401);
    }
  }

  return parse<T>(res);
}

// --- Typed endpoint helpers -------------------------------------------------

import type {
  ProfileType,
  Gender,
  AdminUserDetail,
  AdminUserUpdate,
  AdminUserCreate,
  DeckResponse,
  BrowseResponse,
  CitySuggestion,
  MyProfile,
  Profile,
  MediaItem,
  MediaPage,
  MediaUrls,
  MediaDetail,
  AlbumSummary,
  AlbumDetail,
  AlbumGrant,
  AlbumAccessRequestInbox,
  AlbumPrivacy,
  ChatGrantableAlbum,
  CommentPage,
  PhotoComment,
  ReportTargetType,
  ConversationsPage,
  MessagesPage,
  DmMessage,
  DeleteMessageScope,
  MessageExpiry,
  ModerationItem,
  AdminUser,
  AdminReport,
  AdminStats,
  AdminSettings,
  ReportStatus,
  MediaStatus,
} from "@/lib/types";

export interface RegisterPartner {
  gender: Gender;
  birthDate: string; // ISO
  bio?: string;
}

export interface RegisterInput {
  email: string;
  password: string;
  username: string;
  profileType: ProfileType;
  seeking: ProfileType[];
  country: string;
  /** UI language at registration; emails are sent in it. */
  locale?: string;
  city?: string;
  cityId?: number;
  /** Virtual-only profile: no location; any submitted city is discarded. */
  virtualOnly?: boolean;
  partners: RegisterPartner[];
  /** Combined 18+/Terms/Privacy affirmation; the server requires `true`. */
  consent: boolean;
  turnstileToken?: string;
}

export interface AuthUser {
  id: string;
  email: string;
  username: string;
  profileType: ProfileType;
  emailVerified: boolean;
  role: import("@/lib/types").UserRole;
  status: import("@/lib/types").UserStatus;
}

export function register(input: RegisterInput) {
  return apiFetch<{ user: AuthUser }>("/auth/register", {
    method: "POST",
    body: input,
  });
}

export function login(email: string, password: string, turnstileToken?: string) {
  return apiFetch<{ user: AuthUser }>("/auth/login", {
    method: "POST",
    body: { email, password, ...(turnstileToken ? { turnstileToken } : {}) },
  });
}

export function getPublicSettings() {
  return apiFetch<import("@/lib/types").PublicSettings>("/settings/public");
}

export function logout() {
  return apiFetch<void>("/auth/logout", { method: "POST" });
}

export interface PendingStatus {
  status: import("@/lib/types").UserStatus;
  emailVerified: boolean;
  /** Tunable waitlist number; null when the perception display is off. */
  waitlistPosition: number | null;
  /** The applicant's own gallery photos — 0 means the review has nothing to show. */
  photoCount: number;
}

/** Fresh membership state for the /pending waiting room (auth-gated). */
export function pendingStatus() {
  return apiFetch<PendingStatus>("/pending/status", { auth: true });
}

export function verifyEmail(token: string) {
  return apiFetch<{ verified: true }>("/auth/verify-email", {
    method: "POST",
    body: { token },
  });
}

export function forgotPassword(email: string, turnstileToken?: string) {
  return apiFetch<{ sent: true }>("/auth/forgot-password", {
    method: "POST",
    body: { email, ...(turnstileToken ? { turnstileToken } : {}) },
  });
}

export function resendVerification(email: string, turnstileToken?: string) {
  return apiFetch<{ sent: true }>("/auth/resend-verification", {
    method: "POST",
    body: { email, ...(turnstileToken ? { turnstileToken } : {}) },
  });
}

export function submitAppeal(email: string, message: string, turnstileToken?: string) {
  return apiFetch<{ received: true }>("/appeals", {
    method: "POST",
    body: { email, message, ...(turnstileToken ? { turnstileToken } : {}) },
  });
}

export function resetPassword(token: string, password: string) {
  return apiFetch<{ reset: true }>("/auth/reset-password", {
    method: "POST",
    body: { token, password },
  });
}

export type SwipeAction = "like" | "pass";

export interface SwipeResult {
  matched: boolean;
  matchId?: string;
}

/** Authenticated compatibility deck. `query` is a pre-built `?…` string. */
export function getDeck(query = "") {
  return apiFetch<DeckResponse>(`/deck${query}`, { auth: true });
}

export function swipe(targetUsername: string, action: SwipeAction) {
  return apiFetch<SwipeResult>("/swipes", {
    method: "POST",
    auth: true,
    body: { targetUsername, action },
  });
}

/** DELETE /api/v1/swipes/[username] — undo a recent pass within the grace window. */
export function undoPass(targetUsername: string) {
  return apiFetch<void>(`/swipes/${enc(targetUsername)}`, { method: "DELETE", auth: true });
}

/** Anonymous public browse. `query` is a pre-built `?…` string. */
export function browse(query = "") {
  return apiFetch<BrowseResponse>(`/browse${query}`);
}

/**
 * City autocomplete for the registration wizard, profile edit and the deck
 * location filter. Anonymous (rate-limited server-side); `country` scopes
 * results and is omitted when empty/absent (global search), `q` is a name
 * prefix.
 */
export function getCities(country: string | undefined, q: string) {
  const p = new URLSearchParams({ q });
  if (country) p.set("country", country);
  return apiFetch<CitySuggestion[]>(`/cities?${p.toString()}`);
}

/** Own profile (owner view includes avatar, seeking, email). */
export function getMyProfile(username: string) {
  return apiFetch<MyProfile>(`/profiles/${encodeURIComponent(username)}`, {
    auth: true,
  });
}

// --- Profiles / media / albums ---------------------------------------------

const enc = encodeURIComponent;

/** Any profile (owner-only fields present when it's yours). Cookies ride along. */
export function getProfile(username: string) {
  return apiFetch<Profile>(`/profiles/${enc(username)}`);
}

export function getProfileMedia(
  username: string,
  page = 1,
  /** Optional grid filter; omit for all kinds. */
  kind?: "image" | "video",
) {
  const query = new URLSearchParams({ page: String(page) });
  if (kind) query.set("kind", kind);
  return apiFetch<MediaPage>(`/profiles/${enc(username)}/media?${query}`);
}

export function getProfileAlbums(username: string) {
  return apiFetch<AlbumSummary[]>(`/profiles/${enc(username)}/albums`);
}

export interface UpdateProfileBody {
  username?: string;
  /** UI language preference; emails follow it. */
  locale?: string;
  city?: string;
  cityId?: number | null;
  /** Virtual-only flag: true also clears the stored city server-side. */
  virtualOnly?: boolean;
  country?: string;
  seeking?: ProfileType[];
  /** `orientation`: curated slug or null to clear; omit to leave untouched. */
  partnerBios?: { id: string; bio: string; orientation?: string | null }[];
  desires?: string[];
  interests?: string[];
  customInterests?: string[];
}

export function updateProfile(body: UpdateProfileBody) {
  return apiFetch<Profile>("/me/profile", { method: "PATCH", auth: true, body });
}

/**
 * DELETE /api/v1/me — permanent GDPR account deletion (photos, matches,
 * messages; no recovery). Requires the current password; on success the
 * server has already cleared both auth cookies. Failure modes: wrong
 * password → 403 FORBIDDEN, brute-force guard (3/h) → 429 RATE_LIMITED.
 */
export function deleteAccount(password: string) {
  return apiFetch<{ ok: true }>("/me", {
    method: "DELETE",
    auth: true,
    body: { password },
  });
}

/**
 * POST /api/v1/me/password — change the current password. Requires the current
 * password; wrong password → 403 FORBIDDEN, brute-force guard (5/h) →
 * 429 RATE_LIMITED. Other sessions are signed out on success.
 */
export function changePassword(currentPassword: string, newPassword: string) {
  return apiFetch<{ ok: true }>("/me/password", {
    method: "POST",
    auth: true,
    body: { currentPassword, newPassword },
  });
}

/**
 * POST /api/v1/me/email — request an email change. Re-auths with the current
 * password; a confirmation link goes to the new address. Failure modes: wrong
 * password → 401, address taken → 409 EMAIL_TAKEN, same/invalid → 400.
 */
export function changeEmail(newEmail: string, password: string) {
  return apiFetch<{ pending: string }>("/me/email", {
    method: "POST",
    auth: true,
    body: { newEmail, password },
  });
}

export function cancelPendingEmail() {
  return apiFetch<void>("/me/email/pending", { method: "DELETE", auth: true });
}

export function revokeOtherSessions(password: string) {
  return apiFetch<{ revoked: number }>("/me/sessions/revoke-others", {
    method: "POST",
    auth: true,
    body: { password },
  });
}

export function confirmEmailChange(token: string) {
  return apiFetch<{ changed: true }>("/auth/confirm-email-change", {
    method: "POST",
    body: { token },
  });
}

export interface BlockedUser {
  username: string;
  profileType: string;
  avatar: { urls: MediaUrls } | null;
}

export function listBlocks() {
  return apiFetch<BlockedUser[]>("/blocks", { method: "GET", auth: true });
}

/** Full media detail (owner, counts, my like state, status for owner). */
export function getMediaById(id: string) {
  return apiFetch<MediaDetail>(`/media/${enc(id)}`);
}

export function deleteMedia(id: string) {
  return apiFetch<void>(`/media/${enc(id)}`, { method: "DELETE", auth: true });
}

/**
 * Serialized media row (`serializeMediaListItem`): upload response and
 * own-media list. Same shape as every other media grid item, presigned
 * variant urls included.
 */
export type UploadedMedia = MediaItem;

/** Stable per-pick key reused by retries so media creation is idempotent. */
export function newUploadIdempotencyKey(): string {
  return typeof globalThis.crypto?.randomUUID === "function"
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Multipart upload. Bypasses the JSON wrapper but keeps the same 401 flow. */
export async function uploadMedia(
  file: File,
  opts: { albumId?: string; idempotencyKey?: string } = {},
): Promise<UploadedMedia> {
  const form = new FormData();
  form.append("file", file);
  if (opts.albumId) form.append("albumId", opts.albumId);

  const headers = opts.idempotencyKey
    ? { "Idempotency-Key": opts.idempotencyKey }
    : undefined;
  let res = await fetch(`${BASE}/media`, {
    method: "POST",
    credentials: "include",
    headers,
    body: form,
  });
  if (res.status === 401) {
    const r = await fetch(`${BASE}/auth/refresh`, { method: "POST", credentials: "include" });
    if (r.ok) {
      res = await fetch(`${BASE}/media`, {
        method: "POST",
        credentials: "include",
        headers,
        body: form,
      });
    }
  }
  return parse<UploadedMedia>(res);
}

/**
 * All of the caller's own gallery media (every moderation status, DM media
 * excluded), in gallery order — first item is the profile cover candidate.
 */
export function getMyMedia() {
  return apiFetch<UploadedMedia[]>("/media", { auth: true });
}

/**
 * Persist a new gallery order (subset-stable server-side); the avatar is
 * re-derived from the new first eligible photo. Resolves on 204.
 */
export function reorderMyMedia(orderedIds: string[]) {
  return apiFetch<void>("/me/media/order", { method: "PATCH", auth: true, body: { orderedIds } });
}

/**
 * Move own gallery media into an album, or out of every album when `albumId`
 * is null. The move is all-or-nothing and the avatar is re-derived
 * server-side. Resolves on 204.
 */
export function moveMyMediaToAlbum(mediaIds: string[], albumId: string | null) {
  return apiFetch<void>("/me/media/album", { method: "PATCH", auth: true, body: { mediaIds, albumId } });
}

// Albums
export function listOwnAlbums() {
  return apiFetch<AlbumSummary[]>("/albums", { auth: true });
}

export function createAlbum(name: string, privacy: AlbumPrivacy) {
  return apiFetch<{ id: string; name: string; privacy: AlbumPrivacy }>("/albums", {
    method: "POST",
    auth: true,
    body: { name, privacy },
  });
}

export function getAlbum(id: string, page = 1) {
  return apiFetch<AlbumDetail>(`/albums/${enc(id)}?page=${page}`);
}

export function updateAlbum(id: string, body: { name?: string; privacy?: AlbumPrivacy }) {
  return apiFetch<{ id: string; name: string; privacy: AlbumPrivacy }>(`/albums/${enc(id)}`, {
    method: "PATCH",
    auth: true,
    body,
  });
}

export function deleteAlbum(id: string) {
  return apiFetch<void>(`/albums/${enc(id)}`, { method: "DELETE", auth: true });
}

export function listAlbumGrants(id: string) {
  return apiFetch<AlbumGrant[]>(`/albums/${enc(id)}/access`, { auth: true });
}

export function grantAlbumAccess(id: string, username: string) {
  return apiFetch<void>(`/albums/${enc(id)}/access`, {
    method: "POST",
    auth: true,
    body: { username },
  });
}

export function revokeAlbumAccess(id: string, username: string) {
  return apiFetch<void>(`/albums/${enc(id)}/access`, {
    method: "DELETE",
    auth: true,
    body: { username },
  });
}

// Album access requests
export function requestAlbumAccess(id: string) {
  return apiFetch<void>(`/albums/${enc(id)}/access-requests`, { method: "POST", auth: true });
}

export function listMyAlbumAccessRequests() {
  return apiFetch<AlbumAccessRequestInbox>("/me/album-access-requests", { auth: true });
}

export function resolveAlbumAccessRequest(id: string, approve: boolean) {
  return apiFetch<void>(`/album-access-requests/${enc(id)}/resolve`, {
    method: "POST",
    auth: true,
    body: { approve },
  });
}

// Album grants from chat
export function listChatGrantableAlbums(conversationId: string) {
  return apiFetch<ChatGrantableAlbum[]>(`/conversations/${enc(conversationId)}/album-grants`, {
    auth: true,
  });
}

export function grantAlbumAccessFromChat(conversationId: string, albumId: string) {
  return apiFetch<DmMessage>(`/conversations/${enc(conversationId)}/album-grants`, {
    method: "POST",
    auth: true,
    body: { albumId },
  });
}

// Photo interactions
export function likePhoto(id: string) {
  return apiFetch<{ liked: boolean; likeCount: number }>(`/photos/${enc(id)}/like`, {
    method: "POST",
    auth: true,
  });
}

export function getComments(id: string, page = 1) {
  return apiFetch<CommentPage>(`/photos/${enc(id)}/comments?page=${page}`);
}

export function addComment(id: string, body: string) {
  return apiFetch<Pick<PhotoComment, "id" | "body" | "createdAt">>(
    `/photos/${enc(id)}/comments`,
    { method: "POST", auth: true, body: { body } },
  );
}

export function deleteComment(id: string) {
  return apiFetch<void>(`/comments/${enc(id)}`, { method: "DELETE", auth: true });
}

export function report(targetType: ReportTargetType, targetId: string, reason: string) {
  return apiFetch<{ id: string; status: string }>("/reports", {
    method: "POST",
    auth: true,
    body: { targetType, targetId, reason },
  });
}

export function blockUser(username: string) {
  return apiFetch<void>("/blocks", { method: "POST", auth: true, body: { username } });
}

export function unblockUser(username: string) {
  return apiFetch<void>(`/blocks/${enc(username)}`, { method: "DELETE", auth: true });
}

// --- Direct messages -------------------------------------------------------

export function getConversations(cursor?: string) {
  return apiFetch<ConversationsPage>(
    `/conversations${cursor ? `?cursor=${enc(cursor)}` : ""}`,
    { auth: true },
  );
}

export function unmatch(matchId: string) {
  return apiFetch<void>(`/matches/${enc(matchId)}`, { method: "DELETE", auth: true });
}

/**
 * "Delete for me": hide the conversation's current history from the caller
 * only — it drops off their list until a new message arrives. No notification.
 */
export function clearConversation(conversationId: string) {
  return apiFetch<{ ok: boolean }>(`/conversations/${enc(conversationId)}/clear`, {
    method: "POST",
    auth: true,
  });
}

/**
 * "Delete for both": hard-delete every message (and its media) for BOTH
 * participants. Irreversible — the UI double-confirms before calling this.
 */
export function deleteConversationForBoth(conversationId: string) {
  return apiFetch<void>(`/conversations/${enc(conversationId)}`, {
    method: "DELETE",
    auth: true,
  });
}

export function getMessages(
  conversationId: string,
  opts: { cursor?: string; since?: string } = {},
) {
  const p = new URLSearchParams();
  if (opts.cursor) p.set("cursor", opts.cursor);
  if (opts.since) p.set("since", opts.since);
  const q = p.toString();
  return apiFetch<MessagesPage>(
    `/conversations/${enc(conversationId)}/messages${q ? `?${q}` : ""}`,
    { auth: true },
  );
}

export function sendTextMessage(conversationId: string, body: string) {
  return apiFetch<DmMessage>(`/conversations/${enc(conversationId)}/messages`, {
    method: "POST",
    auth: true,
    body: { type: "text", body },
  });
}

export function sendPhotoMessage(
  conversationId: string,
  mediaId: string,
  expiry: MessageExpiry,
) {
  return apiFetch<DmMessage>(`/conversations/${enc(conversationId)}/messages`, {
    method: "POST",
    auth: true,
    body: { type: "photo", mediaId, expiry },
  });
}

/** Send a video message referencing the sender's own DM video upload (no expiry options). */
export function sendVideoMessage(conversationId: string, mediaId: string) {
  return apiFetch<DmMessage>(`/conversations/${enc(conversationId)}/messages`, {
    method: "POST",
    auth: true,
    body: { type: "video", mediaId },
  });
}

/** Send a voice message referencing the sender's own DM audio upload (no expiry options). */
export function sendAudioMessage(conversationId: string, mediaId: string) {
  return apiFetch<DmMessage>(`/conversations/${enc(conversationId)}/messages`, {
    method: "POST",
    auth: true,
    body: { type: "audio", mediaId },
  });
}

/** Reveal a view-once photo exactly once; returns the message with its urls. */
export function viewMessage(messageId: string) {
  return apiFetch<DmMessage>(`/messages/${enc(messageId)}/view`, {
    method: "POST",
    auth: true,
  });
}

/**
 * Delete a message (idempotent). `scope: "me"` hides it from your own view
 * only (works on any message); `scope: "everyone"` tombstones your own
 * message for both participants.
 */
export function deleteMessage(messageId: string, scope: DeleteMessageScope) {
  return apiFetch<{ ok: boolean }>(`/messages/${enc(messageId)}`, {
    method: "DELETE",
    auth: true,
    body: { scope },
  });
}

/** Upload a photo for a DM (moderation-free `context=dm` channel). */
export async function uploadDmMedia(file: File): Promise<{ id: string }> {
  const form = new FormData();
  form.append("file", file);
  form.append("context", "dm");
  let res = await fetch(`${BASE}/media`, { method: "POST", credentials: "include", body: form });
  if (res.status === 401) {
    const r = await fetch(`${BASE}/auth/refresh`, { method: "POST", credentials: "include" });
    if (r.ok) res = await fetch(`${BASE}/media`, { method: "POST", credentials: "include", body: form });
  }
  return parse<{ id: string }>(res);
}

// --- Push notifications ------------------------------------------------------

export interface PushStatus {
  /** VAPID public key to subscribe with; null = push disabled server-side. */
  vapidPublicKey: string | null;
  /**
   * Whether the user has a subscription on ANY device. Kept for future use;
   * the UI derives its per-device toggle state from the local browser
   * subscription instead (see usePushSubscription).
   */
  subscribed: boolean;
}

export interface PushSubscriptionInput {
  endpoint: string;
  p256dh: string;
  auth: string;
  userAgent?: string;
}

export function getPushStatus() {
  return apiFetch<PushStatus>("/push/subscriptions", { auth: true });
}

export function subscribePush(input: PushSubscriptionInput) {
  return apiFetch<{ ok: true }>("/push/subscriptions", {
    method: "POST",
    auth: true,
    body: input,
  });
}

export function unsubscribePush(endpoint: string) {
  return apiFetch<{ ok: true }>(`/push/subscriptions?endpoint=${enc(endpoint)}`, {
    method: "DELETE",
    auth: true,
  });
}

// --- Admin -----------------------------------------------------------------

export function adminModerationQueue(cursor?: string) {
  return apiFetch<{ items: ModerationItem[]; nextCursor: string | null }>(
    `/admin/moderation${cursor ? `?cursor=${enc(cursor)}` : ""}`,
    { auth: true },
  );
}

export function adminModerate(
  mediaId: string,
  action: "approve" | "reject",
  opts: { reason?: string } = {},
) {
  return apiFetch<void>(`/admin/moderation/${enc(mediaId)}`, {
    method: "POST",
    auth: true,
    body: {
      action,
      ...(opts.reason ? { reason: opts.reason } : {}),
    },
  });
}

export function adminReports(status?: ReportStatus, cursor?: string) {
  const p = new URLSearchParams();
  if (status) p.set("status", status);
  if (cursor) p.set("cursor", cursor);
  const q = p.toString();
  return apiFetch<{ items: AdminReport[]; nextCursor: string | null }>(
    `/admin/reports${q ? `?${q}` : ""}`,
    { auth: true },
  );
}

export function adminResolveReport(
  id: string,
  action: "dismiss" | "hide_content" | "ban_user",
  note?: string,
) {
  return apiFetch<{ id: string; status: ReportStatus }>(`/admin/reports/${enc(id)}/resolve`, {
    method: "POST",
    auth: true,
    body: { action, ...(note ? { note } : {}) },
  });
}

export function adminUsers(
  opts: { search?: string; status?: "active" | "banned" | "rejected"; cursor?: string } = {},
) {
  const p = new URLSearchParams();
  if (opts.search) p.set("search", opts.search);
  if (opts.status) p.set("status", opts.status);
  if (opts.cursor) p.set("cursor", opts.cursor);
  const q = p.toString();
  return apiFetch<{ items: AdminUser[]; nextCursor: string | null }>(
    `/admin/users${q ? `?${q}` : ""}`,
    { auth: true },
  );
}

export function adminBanUser(id: string, reason?: string) {
  return apiFetch<void>(`/admin/users/${enc(id)}/ban`, {
    method: "POST",
    auth: true,
    body: reason ? { reason } : {},
  });
}

export function adminUnbanUser(id: string) {
  return apiFetch<void>(`/admin/users/${enc(id)}/unban`, { method: "POST", auth: true });
}

export function adminSetRole(id: string, role: "user" | "moderator" | "admin") {
  return apiFetch<void>(`/admin/users/${enc(id)}/role`, {
    method: "PATCH",
    auth: true,
    body: { role },
  });
}

export function adminGetUser(id: string) {
  return apiFetch<AdminUserDetail>(`/admin/users/${enc(id)}`, { auth: true });
}

export function adminUpdateUser(id: string, body: AdminUserUpdate) {
  return apiFetch<AdminUserDetail>(`/admin/users/${enc(id)}`, {
    method: "PATCH",
    auth: true,
    body,
  });
}

export function adminCreateUser(body: AdminUserCreate) {
  return apiFetch<AdminUserDetail>(`/admin/users`, {
    method: "POST",
    auth: true,
    body,
  });
}

export function adminDeleteUser(id: string) {
  return apiFetch<void>(`/admin/users/${enc(id)}`, { method: "DELETE", auth: true });
}

/** A member's gallery media, every moderation status, in gallery order (admin-only). */
export function adminUserMedia(id: string) {
  return apiFetch<UploadedMedia[]>(`/admin/users/${enc(id)}/media`, { auth: true });
}

/**
 * Multipart upload on the member's behalf; the result is immediately approved.
 * Mirrors {@link uploadMedia}: bypasses the JSON wrapper but keeps the 401 flow.
 */
export async function adminUploadUserMedia(id: string, file: File): Promise<UploadedMedia> {
  const form = new FormData();
  form.append("file", file);

  const path = `${BASE}/admin/users/${enc(id)}/media`;
  let res = await fetch(path, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (res.status === 401) {
    const r = await fetch(`${BASE}/auth/refresh`, { method: "POST", credentials: "include" });
    if (r.ok) {
      res = await fetch(path, { method: "POST", credentials: "include", body: form });
    }
  }
  return parse<UploadedMedia>(res);
}

/** Persist a new gallery order for the member (admin-only). Resolves on 204. */
export function adminReorderUserMedia(id: string, orderedIds: string[]) {
  return apiFetch<void>(`/admin/users/${enc(id)}/media`, { method: "PATCH", auth: true, body: { orderedIds } });
}

/** Set the member's gallery media status directly, bypassing the pending-only queue (admin-only). Resolves on 204. */
export function adminSetUserMediaStatus(id: string, mediaId: string, status: "approved" | "rejected") {
  return apiFetch<void>(`/admin/users/${enc(id)}/media/${enc(mediaId)}`, {
    method: "PATCH",
    auth: true,
    body: { status },
  });
}

/** Delete the member's media including its stored objects (admin-only). Resolves on 204. */
export function adminDeleteUserMedia(id: string, mediaId: string) {
  return apiFetch<void>(`/admin/users/${enc(id)}/media/${enc(mediaId)}`, { method: "DELETE", auth: true });
}

export function adminStats() {
  return apiFetch<AdminStats>("/admin/stats", { auth: true });
}

export interface AdminAppeal {
  id: string;
  message: string;
  createdAt: string;
  user: { username: string; email: string; bannedAt: string | null; banReason: string | null };
}

export function adminAppeals() {
  return apiFetch<AdminAppeal[]>("/admin/appeals", { auth: true });
}

export function adminResolveAppeal(id: string, unban: boolean, note?: string) {
  return apiFetch<void>(`/admin/appeals/${enc(id)}/resolve`, {
    method: "POST",
    auth: true,
    body: { unban, ...(note ? { note } : {}) },
  });
}

export interface AdminApplicationMedia {
  id: string;
  urls: MediaUrls;
  blurhash: string;
  status: MediaStatus;
  width: number;
  height: number;
}

export interface AdminApplicationPartner {
  gender: "m" | "f";
  birthDate: string;
  bio: string | null;
}

export interface AdminApplication {
  id: string;
  username: string;
  email: string;
  profileType: ProfileType;
  coupleComposition: string | null;
  seeking: ProfileType[];
  desires: string[];
  interests: string[];
  customInterests: string[];
  country: string;
  city: string | null;
  virtualOnly: boolean;
  locale: string;
  emailVerified: boolean;
  createdAt: string;
  partners: AdminApplicationPartner[];
  media: AdminApplicationMedia[];
}

export function adminApplications(cursor?: string) {
  return apiFetch<{ items: AdminApplication[]; nextCursor: string | null }>(
    `/admin/applications${cursor ? `?cursor=${enc(cursor)}` : ""}`,
    { auth: true },
  );
}

export function adminApproveApplication(id: string) {
  return apiFetch<void>(`/admin/applications/${enc(id)}/approve`, { method: "POST", auth: true });
}

export function adminRejectApplication(id: string, reason: string) {
  return apiFetch<void>(`/admin/applications/${enc(id)}/reject`, {
    method: "POST",
    auth: true,
    body: { reason },
  });
}

/** Move a rejected application back to pending for another review round. */
export function adminReconsiderApplication(id: string) {
  return apiFetch<void>(`/admin/applications/${enc(id)}/reconsider`, { method: "POST", auth: true });
}

export function adminGetSettings() {
  return apiFetch<AdminSettings>("/admin/settings", { auth: true });
}

export function adminUpdateSettings(body: Partial<AdminSettings>) {
  return apiFetch<AdminSettings>("/admin/settings", { method: "PATCH", auth: true, body });
}

// --- Admin: blog -------------------------------------------------------------

/** One localized rendering of a post, as the admin API serializes it. */
export interface AdminBlogTranslation {
  locale: string;
  slug: string;
  title: string;
  excerpt: string;
  bodyMd: string;
  metaTitle: string;
  metaDescription: string;
}

export interface AdminBlogPost {
  id: string;
  status: "draft" | "published";
  publishedAt: string | null;
  coverKey: string | null;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  translations: AdminBlogTranslation[];
}

/** Subset PATCH body (`blogUpdateSchema`): translations upsert by locale. */
export interface AdminBlogUpdate {
  tags?: string[];
  coverKey?: string | null;
  translations?: AdminBlogTranslation[];
}

export function adminBlogPosts() {
  return apiFetch<AdminBlogPost[]>("/admin/blog", { auth: true });
}

export function adminUpdateBlogPost(id: string, body: AdminBlogUpdate) {
  return apiFetch<AdminBlogPost>(`/admin/blog/${enc(id)}`, {
    method: "PATCH",
    auth: true,
    body,
  });
}

export function adminPublishBlogPost(id: string) {
  return apiFetch<AdminBlogPost>(`/admin/blog/${enc(id)}`, {
    method: "PATCH",
    auth: true,
    body: { status: "published" },
  });
}

export function adminUnpublishBlogPost(id: string) {
  return apiFetch<AdminBlogPost>(`/admin/blog/${enc(id)}`, {
    method: "PATCH",
    auth: true,
    body: { status: "draft" },
  });
}

/** Delete a draft. The server rejects deleting published posts (409). */
export function adminDeleteBlogDraft(id: string) {
  return apiFetch<void>(`/admin/blog/${enc(id)}`, { method: "DELETE", auth: true });
}
